import { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  LinearProgress,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import AutoStoriesIcon from '@mui/icons-material/AutoStories';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import LayersIcon from '@mui/icons-material/Layers';
import PeopleIcon from '@mui/icons-material/People';
import TokenIcon from '@mui/icons-material/Token';
import { useNavigate } from '@tanstack/react-router';
import { useBooks, useCreateBook } from '../api/queries/books';
import { useCreditsBalance } from '../api/queries/credits';
import { useAuth } from '../auth/useAuth';
import { FilterableSelect } from '../components/common/FilterableSelect';

/** Format a date string as a relative time like "2 hours ago". */
function timeAgo(dateStr: string): string {
  const date = new Date(dateStr);
  const now = Date.now();
  const seconds = Math.floor((now - date.getTime()) / 1000);

  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  return `${months}mo ago`;
}

function statusColor(
  status: string,
): 'default' | 'success' | 'warning' | 'info' {
  switch (status.toLowerCase()) {
    case 'published':
      return 'success';
    case 'draft':
      return 'default';
    case 'in_progress':
      return 'info';
    default:
      return 'default';
  }
}

export function DashboardRoute() {
  const auth = useAuth();
  const navigate = useNavigate();
  const {
    data: books,
    isLoading: booksLoading,
    isError: booksError,
  } = useBooks();
  const { data: creditsData } = useCreditsBalance();
  const [createOpen, setCreateOpen] = useState(false);

  // Recent books: last 6 sorted by updatedAt descending
  const recentBooks = useMemo(() => {
    if (!books) return [];
    return [...books]
      .sort(
        (a, b) =>
          new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
      )
      .slice(0, 6);
  }, [books]);

  // The most recently edited book for "Continue Writing"
  const lastEditedBook = recentBooks[0] ?? null;

  // Aggregate stats
  const totalBooks = books?.length ?? 0;
  const totalChapters = books?.reduce((sum, b) => sum + b.chapterCount, 0) ?? 0;
  const draftCount =
    books?.filter((b) => b.status.toLowerCase() === 'draft').length ?? 0;
  const publishedCount =
    books?.filter((b) => b.status.toLowerCase() === 'published').length ?? 0;

  return (
    <Box>
      {/* Welcome header */}
      <Typography variant="h4" fontWeight={700} gutterBottom>
        Welcome back, {auth.email ?? 'Creator'}
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Your creative workspace
      </Typography>

      {/* Quick-start cards */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Card variant="outlined">
            <CardActionArea
              onClick={() => setCreateOpen(true)}
              sx={{ p: 2, textAlign: 'center' }}
            >
              <AddIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
              <Typography variant="subtitle1" fontWeight={600}>
                New Book
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Start a fresh project
              </Typography>
            </CardActionArea>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 4 }}>
          <Card variant="outlined">
            <CardActionArea
              disabled={!lastEditedBook}
              onClick={() => {
                if (lastEditedBook) {
                  navigate({
                    to: '/books/$bookId/resume',
                    params: { bookId: lastEditedBook.id },
                  });
                }
              }}
              sx={{ p: 2, textAlign: 'center' }}
            >
              <EditIcon sx={{ fontSize: 40, color: 'secondary.main', mb: 1 }} />
              <Typography variant="subtitle1" fontWeight={600}>
                Continue Writing
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap>
                {lastEditedBook ? lastEditedBook.title : 'No books yet'}
              </Typography>
            </CardActionArea>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 4 }}>
          <Card variant="outlined">
            <CardActionArea
              onClick={() => navigate({ to: '/books' })}
              sx={{ p: 2, textAlign: 'center' }}
            >
              <AutoStoriesIcon
                sx={{ fontSize: 40, color: 'info.main', mb: 1 }}
              />
              <Typography variant="subtitle1" fontWeight={600}>
                Browse Books
              </Typography>
              <Typography variant="caption" color="text.secondary">
                View all your books
              </Typography>
            </CardActionArea>
          </Card>
        </Grid>
      </Grid>

      {/* Recent Books */}
      <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
        Recent Books
      </Typography>

      {booksLoading && (
        <Box sx={{ py: 4, textAlign: 'center' }}>
          <CircularProgress size={32} />
        </Box>
      )}
      {booksError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load books.
        </Alert>
      )}

      {!booksLoading && !booksError && recentBooks.length === 0 && (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', mb: 4 }}>
          <Typography color="text.secondary">
            No books yet. Click "New Book" above to get started.
          </Typography>
        </Paper>
      )}

      {recentBooks.length > 0 && (
        <Grid container spacing={2} sx={{ mb: 4 }}>
          {recentBooks.map((book) => (
            <Grid key={book.id} size={{ xs: 12, sm: 6, md: 4, lg: 2 }}>
              <Card variant="outlined" sx={{ height: '100%' }}>
                <CardActionArea
                  onClick={() =>
                    navigate({
                      to: '/books/$bookId/resume',
                      params: { bookId: book.id },
                    })
                  }
                  sx={{ height: '100%' }}
                >
                  <CardContent>
                    <Typography
                      variant="subtitle2"
                      fontWeight={600}
                      noWrap
                      gutterBottom
                    >
                      {book.title}
                    </Typography>
                    <Stack
                      direction="row"
                      spacing={0.5}
                      alignItems="center"
                      sx={{ mb: 1 }}
                    >
                      <Chip
                        size="small"
                        label={book.status}
                        color={statusColor(book.status)}
                        variant="outlined"
                      />
                      <Chip
                        size="small"
                        label={`${book.chapterCount} ch`}
                        variant="outlined"
                      />
                    </Stack>
                    <Typography variant="caption" color="text.secondary">
                      {timeAgo(book.updatedAt)}
                    </Typography>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Bottom section: Writing Stats + Quick Stats */}
      <Grid container spacing={3}>
        {/* Writing Stats */}
        <Grid size={{ xs: 12, md: 7 }}>
          <Paper variant="outlined" sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Writing Stats
            </Typography>
            <Stack spacing={2}>
              <Box>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  sx={{ mb: 0.5 }}
                >
                  <Typography variant="body2">Draft books</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {draftCount} / {totalBooks}
                  </Typography>
                </Stack>
                <LinearProgress
                  variant="determinate"
                  value={totalBooks > 0 ? (draftCount / totalBooks) * 100 : 0}
                  sx={{ height: 8, borderRadius: 4 }}
                />
              </Box>
              <Box>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  sx={{ mb: 0.5 }}
                >
                  <Typography variant="body2">Published books</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {publishedCount} / {totalBooks}
                  </Typography>
                </Stack>
                <LinearProgress
                  variant="determinate"
                  value={
                    totalBooks > 0 ? (publishedCount / totalBooks) * 100 : 0
                  }
                  color="success"
                  sx={{ height: 8, borderRadius: 4 }}
                />
              </Box>
              <Box>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  sx={{ mb: 0.5 }}
                >
                  <Typography variant="body2">Avg chapters per book</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {totalBooks > 0
                      ? (totalChapters / totalBooks).toFixed(1)
                      : '0'}
                  </Typography>
                </Stack>
                <LinearProgress
                  variant="determinate"
                  value={Math.min(
                    100,
                    totalBooks > 0
                      ? (totalChapters / totalBooks / 20) * 100
                      : 0,
                  )}
                  color="info"
                  sx={{ height: 8, borderRadius: 4 }}
                />
              </Box>
            </Stack>
          </Paper>
        </Grid>

        {/* Quick Stats */}
        <Grid size={{ xs: 12, md: 5 }}>
          <Paper variant="outlined" sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Quick Stats
            </Typography>
            <Stack spacing={1.5} divider={<Divider />}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Stack direction="row" spacing={1} alignItems="center">
                  <MenuBookIcon fontSize="small" color="primary" />
                  <Typography variant="body2">Books</Typography>
                </Stack>
                <Typography variant="h6" fontWeight={700}>
                  {totalBooks}
                </Typography>
              </Stack>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Stack direction="row" spacing={1} alignItems="center">
                  <LayersIcon fontSize="small" color="info" />
                  <Typography variant="body2">Chapters</Typography>
                </Stack>
                <Typography variant="h6" fontWeight={700}>
                  {totalChapters}
                </Typography>
              </Stack>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Stack direction="row" spacing={1} alignItems="center">
                  <PeopleIcon fontSize="small" color="secondary" />
                  <Typography variant="body2">Entities</Typography>
                </Stack>
                <Typography
                  variant="h6"
                  fontWeight={700}
                  color="text.secondary"
                >
                  --
                </Typography>
              </Stack>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Stack direction="row" spacing={1} alignItems="center">
                  <TokenIcon fontSize="small" sx={{ color: 'warning.main' }} />
                  <Typography variant="body2">Credits</Typography>
                </Stack>
                <Typography variant="h6" fontWeight={700}>
                  {creditsData?.balance?.toLocaleString() ?? '--'}
                </Typography>
              </Stack>
            </Stack>
          </Paper>
        </Grid>
      </Grid>

      {/* Create book dialog — reuses the same pattern as BooksRoute */}
      <CreateBookDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
      />
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Create Book Dialog (extracted to match the BooksRoute pattern)
// ---------------------------------------------------------------------------
function CreateBookDialog({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const create = useCreateBook();
  const navigate = useNavigate();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [pov, setPov] = useState('third_limited');
  const [tense, setTense] = useState('past');
  const [genre, setGenre] = useState('');

  const handleSubmit = async () => {
    if (!title.trim()) return;
    const result = await create.mutateAsync({
      title: title.trim(),
      description,
      pov,
      tense,
      genre,
    });
    setTitle('');
    setDescription('');
    setGenre('');
    onClose();
    if (result?.data?.id) {
      navigate({
        to: '/books/$bookId/resume',
        params: { bookId: result.data.id },
      });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>New book</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <TextField
            label="Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            autoFocus
            required
          />
          <TextField
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            multiline
            minRows={2}
          />
          <TextField
            label="Genre"
            value={genre}
            onChange={(e) => setGenre(e.target.value)}
          />
          <FilterableSelect
            label="Point of view"
            value={pov}
            onChange={(newValue) => setPov(newValue)}
            allowEmpty={false}
            options={[
              { value: 'first', label: 'First person' },
              { value: 'third_limited', label: 'Third person, limited' },
              { value: 'third_omniscient', label: 'Third person, omniscient' },
            ]}
          />
          <FilterableSelect
            label="Tense"
            value={tense}
            onChange={(newValue) => setTense(newValue)}
            allowEmpty={false}
            options={[
              { value: 'past', label: 'Past' },
              { value: 'present', label: 'Present' },
              { value: 'future', label: 'Future' },
            ]}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!title.trim() || create.isPending}
        >
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
}
